import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getApiBaseUrlForFetch } from "~/constants/common";
import { prepareHeaders } from "~/store/prepareHeaders";

export enum ApiTagType {
  EventsAggregated = "EventsAggregated",
  EventsAggregatedMarkets = "EventsAggregatedMarkets",
  SavedFilters = "SavedFilters",
  Watchlist = "Watchlist",
}

export interface IApiExtraOptions {
  latestOnly?: boolean;
}

const inFlightByEndpoint = new Map<string, AbortController>();

export const dynamicFetchBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  IApiExtraOptions
> = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: getApiBaseUrlForFetch(),
    prepareHeaders,
  });

  const isLatestOnly =
    extraOptions?.latestOnly === true &&
    api.type === "query" &&
    typeof window !== "undefined";

  if (!isLatestOnly) {
    return baseQuery(args, api, extraOptions);
  }

  inFlightByEndpoint.get(api.endpoint)?.abort();

  const controller = new AbortController();
  inFlightByEndpoint.set(api.endpoint, controller);

  if (api.signal.aborted) {
    controller.abort();
  }
  api.signal.addEventListener("abort", () => controller.abort());

  try {
    return await baseQuery(
      args,
      { ...api, signal: controller.signal },
      extraOptions
    );
  } finally {
    if (inFlightByEndpoint.get(api.endpoint) === controller) {
      inFlightByEndpoint.delete(api.endpoint);
    }
  }
};

const CACHE_SECONDS = 60 * 5; // 5 minute

export const api = createApi({
  reducerPath: "api",
  baseQuery: dynamicFetchBaseQuery,
  keepUnusedDataFor: CACHE_SECONDS,
  tagTypes: Object.values(ApiTagType),
  endpoints: () => ({}),
});
